import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  BarChart3,
  Download,
  Plus,
  Trash2,
  Play,
  Calendar,
  Clock,
  Filter,
  FileSpreadsheet,
  RefreshCw,
  Loader2,
  Mail,
  Users,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
const REPORT_TYPES = [
  { value: 'users', label: 'Users', description: 'Participant data' },
  { value: 'assessments', label: 'Assessments', description: 'EDGE & CORE diagnostic data' },
  { value: 'sessions', label: 'Sessions', description: 'Facilitated session records' },
  { value: 'applications', label: 'Applications', description: 'PATH applications' },
  { value: 'bookings', label: 'Bookings', description: 'Session bookings' },
  { value: 'emails', label: 'Emails', description: 'Email send logs' }
];

const FILTER_OPERATORS = [
  { value: 'eq', label: 'Equals' },
  { value: 'ne', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'gt', label: 'Greater Than' },
  { value: 'lt', label: 'Less Than' },
  { value: 'gte', label: 'Greater or Equal' },
  { value: 'lte', label: 'Less or Equal' }
];

const SCHEDULE_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

export default function AdminReports() {
  const [activeTab, setActiveTab] = useState('generate');
  const [loading, setLoading] = useState(false);
  const [scheduledReports, setScheduledReports] = useState([]);
  const [reportResults, setReportResults] = useState(null);
  
  // Report Generation State
  const [reportType, setReportType] = useState('users');
  const [filters, setFilters] = useState([]);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [groupBy, setGroupBy] = useState('');
  
  // Scheduled Report Dialog
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    reportType: 'users',
    schedule: 'weekly',
    recipients: '',
    filters: []
  });

  useEffect(() => {
    if (activeTab === 'scheduled') {
      fetchScheduledReports();
    }
  }, [activeTab]);

  const fetchScheduledReports = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/reports/scheduled');
      setScheduledReports(response.data.reports || []);
    } catch (error) {
      toast.error('Failed to load scheduled reports');
    } finally {
      setLoading(false);
    }
  };

  const addFilter = () => {
    setFilters([...filters, { field: '', operator: 'eq', value: '' }]);
  };

  const updateFilter = (index, key, value) => {
    const newFilters = [...filters];
    newFilters[index][key] = value;
    setFilters(newFilters);
  };

  const removeFilter = (index) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const generateReport = async () => {
    try {
      setLoading(true);
      const payload = {
        reportType,
        filters: filters.filter(f => f.field && f.value),
        dateRange: dateRange.start || dateRange.end ? dateRange : null,
        groupBy: groupBy || null,
        limit: 1000
      };
      
      const response = await axios.post('/api/admin/reports/generate', payload);
      setReportResults(response.data);
      toast.success(`Report generated: ${response.data.summary.totalRecords} records`);
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async () => {
    try {
      setLoading(true);
      const payload = {
        reportType,
        filters: filters.filter(f => f.field && f.value),
        dateRange: dateRange.start || dateRange.end ? dateRange : null,
        limit: 10000
      };
      
      const response = await axios.post('/api/admin/reports/export', payload, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${reportType}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setLoading(false);
    }
  };

  const createScheduledReport = async () => {
    if (!scheduleForm.name || !scheduleForm.recipients) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setLoading(true);
      const payload = {
        name: scheduleForm.name,
        reportType: scheduleForm.reportType,
        schedule: scheduleForm.schedule,
        recipients: scheduleForm.recipients.split(',').map(e => e.trim()),
        filters: scheduleForm.filters,
        isActive: true
      };
      
      await axios.post('/api/admin/reports/scheduled', payload);
      toast.success('Scheduled report created');
      setShowScheduleDialog(false);
      setScheduleForm({ name: '', reportType: 'users', schedule: 'weekly', recipients: '', filters: [] });
      fetchScheduledReports();
    } catch (error) {
      toast.error('Failed to create scheduled report');
    } finally {
      setLoading(false);
    }
  };

  const runScheduledReport = async (reportId) => {
    try {
      setLoading(true);
      const response = await axios.post(`/api/admin/reports/scheduled/${reportId}/run`);
      toast.success(response.data.message);
      fetchScheduledReports();
    } catch (error) {
      toast.error('Failed to run report');
    } finally {
      setLoading(false);
    }
  };

  const deleteScheduledReport = async (reportId) => {
    if (!window.confirm('Delete this scheduled report?')) return;
    
    try {
      await axios.delete(`/api/admin/reports/scheduled/${reportId}`);
      toast.success('Report deleted');
      fetchScheduledReports();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div data-testid="admin-reports">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <FileSpreadsheet className="w-6 h-6 text-primary" />
              Reports & Analytics
            </h1>
            <p className="text-muted-foreground">
              Generate custom reports and schedule automated exports
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="generate">
              <BarChart3 className="w-4 h-4 mr-2" />
              Generate Report
            </TabsTrigger>
            <TabsTrigger value="scheduled">
              <Calendar className="w-4 h-4 mr-2" />
              Scheduled Reports
            </TabsTrigger>
          </TabsList>

          {/* Generate Report Tab */}
          <TabsContent value="generate" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Report Configuration */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    Report Configuration
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Report Type */}
                  <div>
                    <Label>Report Type</Label>
                    <Select value={reportType} onValueChange={setReportType}>
                      <SelectTrigger data-testid="report-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REPORT_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            <div>
                              <span className="font-medium">{type.label}</span>
                              <span className="text-xs text-muted-foreground ml-2">{type.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date Range */}
                  <div className="space-y-2">
                    <Label>Date Range</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="date"
                        value={dateRange.start}
                        onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                        placeholder="Start"
                      />
                      <Input
                        type="date"
                        value={dateRange.end}
                        onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                        placeholder="End"
                      />
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Filters</Label>
                      <Button variant="ghost" size="sm" onClick={addFilter}>
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                    
                    {filters.map((filter, index) => (
                      <div key={index} className="flex gap-2 items-start">
                        <Input
                          placeholder="Field"
                          value={filter.field}
                          onChange={(e) => updateFilter(index, 'field', e.target.value)}
                          className="flex-1"
                        />
                        <Select
                          value={filter.operator}
                          onValueChange={(v) => updateFilter(index, 'operator', v)}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FILTER_OPERATORS.map(op => (
                              <SelectItem key={op.value} value={op.value}>
                                {op.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Value"
                          value={filter.value}
                          onChange={(e) => updateFilter(index, 'value', e.target.value)}
                          className="flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFilter(index)}
                          className="text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  {/* Group By */}
                  <div>
                    <Label>Group By (Optional)</Label>
                    <Input
                      value={groupBy}
                      onChange={(e) => setGroupBy(e.target.value)}
                      placeholder="e.g., status, type"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-4">
                    <Button onClick={generateReport} disabled={loading} className="flex-1">
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                      Generate
                    </Button>
                    <Button variant="outline" onClick={exportReport} disabled={loading}>
                      <Download className="w-4 h-4 mr-2" />
                      CSV
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Results */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Results</CardTitle>
                  {reportResults && (
                    <CardDescription>
                      {reportResults.summary.totalRecords} records • Generated {formatDate(reportResults.summary.generatedAt)}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  {!reportResults ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Configure and generate a report to see results</p>
                    </div>
                  ) : reportResults.results.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>No data matching your criteria</p>
                    </div>
                  ) : (
                    <div className="overflow-auto max-h-[500px]">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            {Object.keys(reportResults.results[0]).slice(0, 6).map(key => (
                              <TableHead key={key} className="capitalize">
                                {key.replace(/([A-Z])/g, ' $1').trim()}
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reportResults.results.slice(0, 50).map((row, i) => (
                            <TableRow key={i}>
                              {Object.entries(row).slice(0, 6).map(([key, value]) => (
                                <TableCell key={key} className="max-w-[200px] truncate">
                                  {typeof value === 'object' ? JSON.stringify(value) : String(value || '-')}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {reportResults.results.length > 50 && (
                        <p className="text-sm text-muted-foreground text-center mt-4">
                          Showing 50 of {reportResults.results.length} results. Export to CSV for full data.
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Scheduled Reports Tab */}
          <TabsContent value="scheduled">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Scheduled Reports</CardTitle>
                  <CardDescription>
                    Automated reports delivered to your inbox
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={fetchScheduledReports} disabled={loading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button onClick={() => setShowScheduleDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    New Schedule
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : scheduledReports.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No scheduled reports yet</p>
                    <Button onClick={() => setShowScheduleDialog(true)} className="mt-4">
                      Create First Schedule
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Report Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Schedule</TableHead>
                        <TableHead>Recipients</TableHead>
                        <TableHead>Last Run</TableHead>
                        <TableHead>Next Run</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scheduledReports.map((report) => (
                        <TableRow key={report.id}>
                          <TableCell className="font-medium">{report.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {report.reportType}
                            </Badge>
                          </TableCell>
                          <TableCell className="capitalize">{report.schedule}</TableCell>
                          <TableCell>
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {report.recipients?.length || 0}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(report.lastRun)}
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatDate(report.nextRun)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={report.isActive ? 'default' : 'secondary'}>
                              {report.isActive ? 'Active' : 'Paused'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => runScheduledReport(report.id)}
                              >
                                <Play className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteScheduledReport(report.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Schedule Dialog */}
        <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Scheduled Report</DialogTitle>
              <DialogDescription>
                Set up an automated report to be delivered on a schedule
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Report Name *</Label>
                <Input
                  value={scheduleForm.name}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                  placeholder="Weekly User Summary"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Report Type</Label>
                  <Select
                    value={scheduleForm.reportType}
                    onValueChange={(v) => setScheduleForm({ ...scheduleForm, reportType: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REPORT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Schedule</Label>
                  <Select
                    value={scheduleForm.schedule}
                    onValueChange={(v) => setScheduleForm({ ...scheduleForm, schedule: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SCHEDULE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Recipients (comma-separated emails) *</Label>
                <Input
                  value={scheduleForm.recipients}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, recipients: e.target.value })}
                  placeholder="admin@example.com, team@example.com"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>
                Cancel
              </Button>
              <Button onClick={createScheduledReport} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Create Schedule
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
  );
}
